import { createEmitter, type Handler as EmitterHandler, type WaitForOptions as EmitterWaitForOptions } from "./emitter"

declare const messagePayload: unique symbol

export interface MessageDefinition<TPayload = void> {
  readonly type: string
  readonly [messagePayload]: TPayload
}

export type AnyMessageDefinition = MessageDefinition<unknown>

export type MessagePayload<TMessage extends AnyMessageDefinition> = TMessage[typeof messagePayload]

type EmitArgs<T> = [T] extends [void] ? [payload?: T] : [payload: T]
type MessengerEventMap = Record<string, unknown>

export type MessageHandler<TPayload> = (payload: TPayload) => void
export type MessageUnsubscribe = () => void

export interface WaitForMessageOptions<TPayload> {
  signal?: AbortSignal
  filter?: (payload: TPayload) => boolean
}

export interface Messenger {
  on<TPayload>(message: MessageDefinition<TPayload>, handler: MessageHandler<TPayload>): MessageUnsubscribe
  once<TPayload>(message: MessageDefinition<TPayload>, handler: MessageHandler<TPayload>): MessageUnsubscribe
  off<TPayload>(message: MessageDefinition<TPayload>, handler: MessageHandler<TPayload>): void
  emit<TPayload>(message: MessageDefinition<TPayload>, ...args: EmitArgs<TPayload>): void
  waitFor<TPayload>(message: MessageDefinition<TPayload>, options?: WaitForMessageOptions<TPayload>): Promise<TPayload>
  clear(message?: AnyMessageDefinition): void
  listenerCount(message?: AnyMessageDefinition): number
}

export interface MessengerOptions {
  /** Application-runtime hook invoked immediately before normal message subscribers. */
  readonly beforeEmit?: (message: AnyMessageDefinition, payload: unknown) => void
}

export function defineMessage<TPayload = void>(type: string): MessageDefinition<TPayload> {
  return Object.freeze({ type }) as MessageDefinition<TPayload>
}

export function createMessenger(options: MessengerOptions = {}): Messenger {
  const emitter = createEmitter<MessengerEventMap>()

  return Object.freeze({
    on<TPayload>(message: MessageDefinition<TPayload>, handler: MessageHandler<TPayload>) {
      return emitter.on(message.type, handler as EmitterHandler<unknown>)
    },
    once<TPayload>(message: MessageDefinition<TPayload>, handler: MessageHandler<TPayload>) {
      return emitter.once(message.type, handler as EmitterHandler<unknown>)
    },
    off<TPayload>(message: MessageDefinition<TPayload>, handler: MessageHandler<TPayload>) {
      emitter.off(message.type, handler as EmitterHandler<unknown>)
    },
    emit<TPayload>(message: MessageDefinition<TPayload>, ...args: EmitArgs<TPayload>) {
      options.beforeEmit?.(message as AnyMessageDefinition, args[0])
      emitter.emit(message.type, args[0])
    },
    waitFor<TPayload>(message: MessageDefinition<TPayload>, options?: WaitForMessageOptions<TPayload>) {
      return emitter.waitFor(message.type, options as EmitterWaitForOptions<unknown> | undefined) as Promise<TPayload>
    },
    clear(message?: AnyMessageDefinition) {
      emitter.clear(message?.type)
    },
    listenerCount(message?: AnyMessageDefinition) {
      return emitter.listenerCount(message?.type)
    },
  } satisfies Messenger)
}
